import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@vehix/portal/components/ui/dialog';
import { Button } from '@vehix/portal/components/ui/button';
import { Input } from '@vehix/portal/components/ui/input';
import { Label } from '@vehix/portal/components/ui/label';
import { Badge } from '@vehix/portal/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@vehix/portal/components/ui/select';
import { Save } from 'lucide-react';
import { ScheduleAdvancedSection } from './ScheduleAdvancedSection';
import { ScheduleWhenSection } from './ScheduleWhenSection';
import { ScheduleSequencePreview } from './ScheduleSequencePreview';
import {
  evaluateSlot,
  oemOf,
  rankSlots,
  type SlotContext
} from '@vehix/shared/admin/utils/scheduleSlots';
import { useAllTenants } from '@vehix/portal/hooks/useTenants';
import {
  useAvailableZones,
  useCreatePipelineSchedule,
  useUpdatePipelineSchedule,
  usePipelineSchedules,
  pipelineKeys
} from '../../hooks/usePipelineOrchestrator';
import { useToast } from '@vehix/shared/admin/portal-bridge';
import {
  type PipelineSchedule,
  type PipelineScheduleCreateRequest,
  type PipelineScheduleUpdateRequest,
  type PipelineScheduleConfig
} from '../../services/pipelineOrchestrator';
import { PipelineApiError } from '../../services/pipelineErrors';
import { PipelineZoneSelector, type ZoneSelection } from './PipelineZoneSelector';
import { SERVER_DEFAULT_RUN_MODE } from '@vehix/shared/admin/utils/scheduleDefaults';
import { buildCronFromPicker } from '@vehix/shared/admin/utils/cronCalendar';
import {
  blankScheduleForm,
  scheduleFormValues
} from '@vehix/shared/admin/utils/scheduleFormValues';
import {
  sameRecipients,
  sharedRecipients as deriveShared
} from '@vehix/shared/admin/utils/scheduleRecipients';
import { useQueryClient } from '@tanstack/react-query';

interface PipelineScheduleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schedule?: PipelineSchedule | null | undefined;
  defaultTenantId?: string | undefined;
}

// eslint-disable-next-line max-lines-per-function
export const PipelineScheduleFormDialog: React.FC<PipelineScheduleFormDialogProps> = ({
  open,
  onOpenChange,
  schedule,
  defaultTenantId
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const createSchedule = useCreatePipelineSchedule();
  const updateSchedule = useUpdatePipelineSchedule();
  const isEditing = !!schedule;

  // Form state
  const [scheduleName, setScheduleName] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [scheduleHour, setScheduleHour] = useState(2);
  const [scheduleMinute, setScheduleMinute] = useState(0);
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]);
  const [customCron, setCustomCron] = useState<string | null>(null);
  const [timeZone, setTimeZone] = useState('America/New_York');
  const [selectedZones, setSelectedZones] = useState<ZoneSelection[]>([]);
  const [runMode, setRunMode] = useState<'auto' | 'full' | 'delta'>('auto');
  const [vinProcessorType, setVinProcessorType] = useState<'api' | 'selenium'>('api');
  const [skipVinProcessing, setSkipVinProcessing] = useState(false);
  const [enableDedup, setEnableDedup] = useState(false);
  const [notificationEmails, setNotificationEmails] = useState<string[]>(['engineering@kebi.ai']);
  const [notifyOnSuccess, setNotifyOnSuccess] = useState(false);
  const [notifyOnFailure, setNotifyOnFailure] = useState(true);
  const [minIntervalMinutes, setMinIntervalMinutes] = useState(60);
  const [description, setDescription] = useState('');
  const [overrideRecipients, setOverrideRecipients] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Data
  const { data: tenants } = useAllTenants();
  const { data: availableZones, isLoading: zonesLoading } = useAvailableZones(tenantId);

  // Every schedule, deliberately unfiltered: a time collision is cross-tenant
  // by nature, so suggestions have to see schedules the page's tenant filter
  // is hiding. Separate cache key from the list's when a filter is active —
  // one extra request, cached five minutes.
  const { data: allSchedules } = usePipelineSchedules({});
  const existingSchedules = useMemo(() => allSchedules?.schedules ?? [], [allSchedules]);

  /**
   * The recipient list managed under Settings. New schedules inherit it, and
   * an existing schedule "overrides" simply by holding a different list.
   */
  const shared = useMemo(() => deriveShared(existingSchedules), [existingSchedules]);

  const selectedTenant = useMemo(
    () => (tenants || []).find(t => t.id === tenantId),
    [tenants, tenantId]
  );

  /**
   * The dealership's own timezone, used only to judge off-hours — never
   * written to the schedule. The tenant records carry some wrong values
   * (a Texas dealer marked Eastern, two Michigan ones marked Central), so
   * propagating them into saved schedules would spread the error.
   */
  const dealerZone = selectedTenant?.time_zone || timeZone;

  // True once the name has been typed in, which stops auto-fill for good.
  const nameTouched = useRef(false);

  /** OEM prefixes this schedule will crawl, from the zones picked so far. */
  const selectedOems = useMemo(
    () => [...new Set(selectedZones.map(z => oemOf(z.zone_slug)).filter(Boolean))],
    [selectedZones]
  );

  const slotContext: SlotContext = useMemo(
    () => ({
      existing: existingSchedules.filter(s => s.id !== schedule?.id),
      oems: selectedOems,
      daysOfWeek,
      scheduleZone: timeZone,
      dealerZone
    }),
    [existingSchedules, schedule?.id, selectedOems, daysOfWeek, timeZone, dealerZone]
  );

  /** Best few times: off-hours for the dealer, finishing before they open, no same-brand clash. */
  const suggestions = useMemo(
    () => (selectedOems.length === 0 ? [] : rankSlots(slotContext).slice(0, 4)),
    [selectedOems, slotContext]
  );

  /** How the currently chosen time scores, for the inline warning. */
  const chosenSlot = useMemo(
    () =>
      selectedOems.length === 0 ? null : evaluateSlot(scheduleHour, scheduleMinute, slotContext),
    [selectedOems, scheduleHour, scheduleMinute, slotContext]
  );

  // Read through refs so the hydrate effect can use the latest values without
  // depending on them. Both change while the dialog is open — the recipient
  // list when its query resolves, the tenant id when the page's filter moves —
  // and either as a dependency would wipe whatever the user had typed.
  const seedRef = useRef({ defaultTenantId, shared });
  seedRef.current = { defaultTenantId, shared };

  // Reset / populate form
  useEffect(() => {
    if (!open) return;
    const seed = seedRef.current;
    const v = schedule
      ? scheduleFormValues(schedule)
      : blankScheduleForm(seed.defaultTenantId, seed.shared);

    nameTouched.current = isEditing;
    // An existing schedule is "overriding" purely by holding a different list
    // — there is no stored flag, so it is inferred the same way Settings does.
    setOverrideRecipients(schedule ? !sameRecipients(v.notificationEmails, seed.shared) : false);
    setScheduleName(v.scheduleName);
    setTenantId(v.tenantId);
    setScheduleHour(v.scheduleHour);
    setScheduleMinute(v.scheduleMinute);
    setDaysOfWeek(v.daysOfWeek);
    setCustomCron(v.customCron);
    setTimeZone(v.timeZone);
    setSelectedZones(v.selectedZones);
    setRunMode(v.runMode);
    setVinProcessorType(v.vinProcessorType);
    setSkipVinProcessing(v.skipVinProcessing);
    setEnableDedup(v.enableDedup);
    setNotificationEmails(v.notificationEmails);
    setNotifyOnSuccess(v.notifyOnSuccess);
    setNotifyOnFailure(v.notifyOnFailure);
    setMinIntervalMinutes(v.minIntervalMinutes);
    setDescription(v.description);
    setAdvancedOpen(false);
    setErrors({});
  }, [open, schedule, isEditing]);

  // Clear zones on tenant change
  useEffect(() => {
    if (!isEditing) setSelectedZones([]);
  }, [tenantId, isEditing]);

  /**
   * Name follows the tenant until someone types their own.
   *
   * Matches the convention already in use — 11 of the 14 live schedules are
   * named "Weekly refresh for <Tenant>".
   */
  useEffect(() => {
    if (!open || isEditing || nameTouched.current) return;
    const name = selectedTenant?.tenant_name;
    setScheduleName(name ? `Weekly refresh for ${name}` : '');
  }, [open, isEditing, selectedTenant]);

  // Recipient add/remove/validate now lives in EmailChipsInput.

  const handleToggleDay = useCallback((day: number) => {
    setDaysOfWeek(prev => (prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]));
  }, []);

  const cronExpression =
    customCron ?? buildCronFromPicker(scheduleHour, scheduleMinute, daysOfWeek);

  // Validation
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!scheduleName.trim()) newErrors['name'] = 'Name is required';
    if (!tenantId) newErrors['tenant'] = 'Tenant is required';
    if (!customCron && daysOfWeek.length === 0) {
      newErrors['cron'] = 'Select at least one day';
    }
    if (selectedZones.length === 0) newErrors['zones'] = 'Select at least one zone';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Map server-side problem+json error pointers back to our local error keys.
  const handleApiError = (err: unknown, fallbackTitle: string) => {
    if (err instanceof PipelineApiError) {
      if (err.status === 422) {
        const fieldErrors = err.fieldErrors();
        setErrors(prev => ({
          ...prev,
          ...(fieldErrors['schedule_name'] && { name: fieldErrors['schedule_name'] }),
          ...(fieldErrors['cron_expression'] && { cron: fieldErrors['cron_expression'] }),
          ...(fieldErrors['pipeline_config.zones'] && {
            zones: fieldErrors['pipeline_config.zones']
          })
        }));
        onOpenChange(true);
        toast({
          title: err.problem.title,
          ...(err.problem.detail && { description: err.problem.detail }),
          variant: 'destructive'
        });
        return;
      }
      if (err.status === 409) {
        setErrors(prev => ({ ...prev, name: 'Already taken for this tenant' }));
        onOpenChange(true);
        toast({ title: 'Schedule name conflict', variant: 'destructive' });
        return;
      }
      if (err.status === 410) {
        queryClient.invalidateQueries({ queryKey: pipelineKeys.schedules });
        toast({ title: 'Schedule was deleted', variant: 'destructive' });
        return;
      }
      if (err.status === 412) {
        queryClient.invalidateQueries({ queryKey: pipelineKeys.schedules });
        onOpenChange(true);
        toast({
          title: 'Schedule was modified by another user — please reapply your changes',
          variant: 'destructive'
        });
        return;
      }
    }
    toast({ title: fallbackTitle, variant: 'destructive' });
  };

  const buildPipelineConfig = (): PipelineScheduleConfig => ({
    zones: selectedZones.flatMap(z =>
      z.collectors.map(collector => ({ zone_slug: z.zone_slug, collector }))
    ),
    // Omitted when it matches the server's default so the schedule keeps
    // inheriting it — NOT tied to the form's initial value.
    ...(runMode !== SERVER_DEFAULT_RUN_MODE && { run_mode: runMode }),
    ...(vinProcessorType !== 'api' && { vin_processor_type: vinProcessorType }),
    ...(skipVinProcessing && { skip_vin_processing: skipVinProcessing }),
    ...(enableDedup && { enable_dedup: true })
  });

  // PATCH semantics: omit/null = leave unchanged. Always send every mutable
  // field so user-initiated clears (empty emails, empty description) propagate.
  // is_enabled is intentionally omitted — toggle lives on the list row.
  const buildUpdatePayload = (
    pipeline_config: PipelineScheduleConfig
  ): PipelineScheduleUpdateRequest => ({
    schedule_name: scheduleName,
    cron_expression: cronExpression,
    time_zone: timeZone,
    pipeline_config,
    notification_emails: notificationEmails,
    notify_on_success: notifyOnSuccess,
    notify_on_failure: notifyOnFailure,
    min_interval_minutes: minIntervalMinutes,
    description
  });

  const buildCreatePayload = (
    pipeline_config: PipelineScheduleConfig
  ): PipelineScheduleCreateRequest => ({
    schedule_name: scheduleName,
    tenant_id: tenantId,
    cron_expression: cronExpression,
    time_zone: timeZone,
    pipeline_config,
    ...(notificationEmails.length > 0 && { notification_emails: notificationEmails }),
    notify_on_success: notifyOnSuccess,
    notify_on_failure: notifyOnFailure,
    min_interval_minutes: minIntervalMinutes,
    ...(description && { description })
  });

  // Submit
  const handleSubmit = () => {
    if (!validate()) return;

    const pipeline_config = buildPipelineConfig();

    // Instant feedback
    onOpenChange(false);
    toast({ title: isEditing ? 'Schedule updated' : 'Schedule created' });

    if (isEditing && schedule) {
      updateSchedule.mutate(
        { id: schedule.id, ...buildUpdatePayload(pipeline_config) },
        { onError: err => handleApiError(err, 'Failed to update schedule') }
      );
      return;
    }

    createSchedule.mutate(
      { data: buildCreatePayload(pipeline_config), idempotencyKey: crypto.randomUUID() },
      { onError: err => handleApiError(err, 'Failed to create schedule') }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Two columns at md and up. The single stack ran past 1,200px tall and
          always scrolled; splitting identity+timing from scope+notifications
          roughly halves it and fits a laptop viewport without a scrollbar. */}
      <DialogContent className='max-h-[90vh] max-w-4xl overflow-y-auto'>
        <DialogHeader className='space-y-1'>
          <DialogTitle>{isEditing ? 'Edit Schedule' : 'Create Pipeline Schedule'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the pipeline schedule configuration.'
              : 'Set up an automated pipeline to run on a recurring schedule.'}
          </DialogDescription>
        </DialogHeader>

        <div className='grid gap-x-6 gap-y-4 py-1 md:grid-cols-2'>
          {/* Left column — who it's for and what it covers. Tenant leads
              because everything else is derived from it: the name, the
              available zones, and the dealer's own hours. */}
          <div className='space-y-4'>
            <div className='space-y-1.5'>
              <Label className='text-xs'>Tenant</Label>
              <Select value={tenantId} onValueChange={setTenantId} disabled={isEditing}>
                <SelectTrigger className={errors['tenant'] ? 'border-red-500' : ''}>
                  <SelectValue placeholder='Select a tenant to begin' />
                </SelectTrigger>
                <SelectContent>
                  {(tenants || []).map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.tenant_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors['tenant'] && <p className='text-xs text-red-500'>{errors['tenant']}</p>}
            </div>

            <div className='space-y-1.5'>
              <Label className='text-xs'>Schedule name</Label>
              <Input
                value={scheduleName}
                onChange={e => {
                  nameTouched.current = true;
                  setScheduleName(e.target.value);
                }}
                placeholder='Pick a tenant to fill this in'
                className={errors['name'] ? 'border-red-500' : ''}
              />
              {errors['name'] && <p className='text-xs text-red-500'>{errors['name']}</p>}
            </div>

            <div className='space-y-1.5'>
              <Label className='text-xs'>
                Zones{' '}
                {selectedZones.length > 0 && (
                  <Badge variant='secondary' className='ml-1'>
                    {selectedZones.length}
                  </Badge>
                )}
              </Label>
              {errors['zones'] && <p className='text-xs text-red-500'>{errors['zones']}</p>}

              <PipelineZoneSelector
                zones={availableZones}
                value={selectedZones}
                onChange={setSelectedZones}
                isLoading={zonesLoading}
                placeholder={tenantId ? undefined : 'Select a tenant first'}
                invalid={!!errors['zones']}
              />
            </div>
          </div>

          {/* Right column — when it runs, how it sequences, who hears about it */}
          <div className='space-y-4'>
            <ScheduleWhenSection
              customCron={customCron}
              setCustomCron={setCustomCron}
              scheduleHour={scheduleHour}
              setScheduleHour={setScheduleHour}
              scheduleMinute={scheduleMinute}
              setScheduleMinute={setScheduleMinute}
              daysOfWeek={daysOfWeek}
              setDaysOfWeek={setDaysOfWeek}
              onToggleDay={handleToggleDay}
              timeZone={timeZone}
              setTimeZone={setTimeZone}
              error={errors['cron']}
              suggestions={suggestions}
              chosenSlot={chosenSlot}
              dealerZone={dealerZone}
              dealerName={selectedTenant?.tenant_name}
            />

            <ScheduleSequencePreview
              existing={existingSchedules}
              oems={selectedOems}
              daysOfWeek={daysOfWeek}
              hour={scheduleHour}
              minute={scheduleMinute}
              scheduleZone={timeZone}
              dealerZone={dealerZone}
              editingScheduleId={schedule?.id}
            />
          </div>

          {/* Advanced spans both columns — it's collapsed by default, so it
              costs one row of height until someone opens it. */}
          <div className='md:col-span-2'>
            <ScheduleAdvancedSection
              runMode={runMode}
              setRunMode={setRunMode}
              advancedOpen={advancedOpen}
              setAdvancedOpen={setAdvancedOpen}
              vinProcessorType={vinProcessorType}
              setVinProcessorType={setVinProcessorType}
              skipVinProcessing={skipVinProcessing}
              setSkipVinProcessing={setSkipVinProcessing}
              enableDedup={enableDedup}
              setEnableDedup={setEnableDedup}
              notifyOnSuccess={notifyOnSuccess}
              setNotifyOnSuccess={setNotifyOnSuccess}
              notifyOnFailure={notifyOnFailure}
              setNotifyOnFailure={setNotifyOnFailure}
              notificationEmails={notificationEmails}
              setNotificationEmails={setNotificationEmails}
              sharedRecipients={shared}
              overrideRecipients={overrideRecipients}
              onOverrideRecipientsChange={on => {
                setOverrideRecipients(on);
                // Turning the override off hands the schedule back to Settings.
                if (!on) setNotificationEmails(shared);
              }}
              minIntervalMinutes={minIntervalMinutes}
              setMinIntervalMinutes={setMinIntervalMinutes}
              description={description}
              setDescription={setDescription}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>
            <Save className='h-4 w-4 mr-1' />
            {isEditing ? 'Save Changes' : 'Create Schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
